import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@material-ui/core'
import { CODE_LENGTH, applyBoxInput, digitsToBoxes, normalizePastedCode } from './passcode'

/**
 * Six single-character boxes that behave like one field.
 *
 * All of the interesting logic lives in ./passcode.js as pure functions -- this component is the
 * wiring (focus movement and the paste event), which is the part a unit test cannot usefully assert
 * and a human can see at a glance.
 */
const PasscodeFields = props => {
  const { boxes, onChange, disabled, autoFocus } = props
  const inputs = useRef([])

  const focus = index => {
    const el = inputs.current[index]
    if (el) {
      el.focus()
      // Select rather than just focus, so typing over a filled box replaces it instead of being
      // rejected as a second character.
      el.select && el.select()
    }
  }

  const handleChange = index => event => {
    const next = applyBoxInput(boxes, index, event.target.value)
    onChange(next.boxes)
    focus(next.focus)
  }

  // Bound to every box, not just the first: an admin who has typed three digits and then pastes is
  // pasting into box four, and the code still has to land whole.
  const handlePaste = index => event => {
    // preventDefault FIRST. With the guard ahead of it, a paste carrying no digits ("hello") fell
    // through to the browser's own paste, landed in the box, fired onChange, and normalised to '' --
    // silently wiping a digit the admin had already typed.
    event.preventDefault()

    const pasted = normalizePastedCode(
      (event.clipboardData || window.clipboardData).getData('text')
    )
    if (!pasted) return
    // A full-length paste always fills the whole field from the start, wherever the caret was --
    // that is what someone copying the code out of the email means by it.
    const next = pasted.length === CODE_LENGTH
      ? digitsToBoxes(pasted)
      : applyBoxInput(boxes, index, pasted).boxes

    onChange(next)
    focus(Math.min(index + pasted.length, CODE_LENGTH - 1))
  }

  const handleKeyDown = index => event => {
    if (event.key === 'Backspace' && !boxes[index] && index > 0) {
      // Step back into the previous box and clear it, so holding backspace empties the field the way
      // it would in a single input.
      event.preventDefault()
      const next = [...boxes]
      next[index - 1] = ''
      onChange(next)
      focus(index - 1)
      return
    }
    if (event.key === 'ArrowLeft' && index > 0) focus(index - 1)
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) focus(index + 1)
  }

  return (
    <div className={'passcode-fields'} data-testid="passcode-fields">
      {boxes.map((value, index) => (
        <TextField
          key={index}
          className={'passcode-box'}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          value={value}
          onChange={handleChange(index)}
          onPaste={handlePaste(index)}
          onKeyDown={handleKeyDown(index)}
          variant="outlined"
          inputProps={{
            'aria-label': `Passcode digit ${index + 1}`,
            // Select on EVERY focus, not just the programmatic moves below. Clicking into a filled
            // box leaves the caret after the digit, so typing a replacement delivers a two-character
            // value (maxLength is the whole code, not 1) -- which applyBoxInput then spreads, keeping
            // the digit being corrected and overwriting the NEXT one instead. Selecting on focus makes
            // click, tab and programmatic focus all behave the same.
            onFocus: event => event.target.select(),
            // inputMode/pattern rather than type="number": a number input strips leading zeros and
            // shows spinner arrows, and a passcode is a string of digits, not a quantity.
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: CODE_LENGTH,
            autoComplete: index === 0 ? 'one-time-code' : 'off',
            ref: el => { inputs.current[index] = el },
          }}
        />
      ))}
    </div>
  )
}

PasscodeFields.propTypes = {
  boxes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
}

export default PasscodeFields
